import {
  maxPurchasable,
  type PurchasableVariant,
} from "~/lib/cart/cart-interface";

/**
 * P3-08 · Derive the buy-box action state from the resolved variant.
 *
 * Pure so the disabled/hint logic is unit-tested without the DOM. The rule:
 * - no resolved+complete variant → disabled, hint = the engine's own
 *   validation message ("Select a size"), so the copy stays in one place.
 * - resolved but nothing purchasable (stock 0, not backorderable) → disabled,
 *   "Out of stock".
 * - otherwise enabled, with `max` bounding the quantity stepper.
 */
export interface ActionState {
  disabled: boolean;
  /** Hint explaining a disabled CTA; null when the CTA is enabled. */
  reason: string | null;
  /** Upper bound for the quantity stepper (0 when nothing is addable). */
  max: number;
}

export function deriveActionState(args: {
  resolvedVariant: PurchasableVariant | undefined;
  isComplete: boolean;
  validationMessage: string | null;
  backorderCap: number;
}): ActionState {
  const { resolvedVariant, isComplete, validationMessage, backorderCap } = args;

  if (!resolvedVariant || !isComplete) {
    return {
      disabled: true,
      reason: validationMessage ?? "Select options",
      max: 0,
    };
  }

  const max = maxPurchasable(resolvedVariant, backorderCap);
  if (max < 1) {
    return { disabled: true, reason: "Out of stock", max: 0 };
  }

  return { disabled: false, reason: null, max };
}
